import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getAnnouncementRecipients, sendAnnouncement, getAnnouncementHistory } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';

const ALL_ROLES = ['admin', 'healer', 'student', 'patient', 'general'];

const Announcements = () => {
  const { isMobile } = useWindowSize();
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('compose'); // 'compose' | 'whatsapp' | 'history'
  const [previewMode, setPreviewMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);

  useEffect(function() {
    if (selectedRoles.length === 0) {
      setRecipients([]);
      setRecipientCount(0);
      return;
    }
    setLoadingRecipients(true);
    getAnnouncementRecipients(selectedRoles)
      .then(function(res) {
        setRecipients(res.data.users || []);
        setRecipientCount(res.data.count || 0);
      })
      .catch(function() {
        setRecipients([]);
        setRecipientCount(0);
      })
      .finally(function() { setLoadingRecipients(false); });
  }, [selectedRoles]); // eslint-disable-line react-hooks/exhaustive-deps

  var toggleRole = function(role) {
    setSelectedRoles(function(prev) {
      if (role === 'all') {
        return prev.length === ALL_ROLES.length ? [] : [...ALL_ROLES];
      }
      return prev.includes(role) ? prev.filter(function(r) { return r !== role; }) : [...prev, role];
    });
    setResult(null);
  };

  var handleSend = async function() {
    if (!selectedRoles.length) { alert('Please select at least one recipient role.'); return; }
    if (!subject.trim()) { alert('Please enter a subject.'); return; }
    if (!bodyHtml.trim()) { alert('Please enter the email body.'); return; }
    if (!window.confirm('Send this announcement to ' + recipientCount + ' recipient(s)?')) return;
    setSending(true);
    setResult(null);
    try {
      var res = await sendAnnouncement({ roles: selectedRoles, subject, bodyHtml });
      setResult(res.data);
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error sending announcement');
    } finally {
      setSending(false);
    }
  };

  useEffect(function() {
    if (activeTab !== 'history') return;
    setHistoryLoading(true);
    getAnnouncementHistory()
      .then(function(res) { setHistory(res.data.announcements || []); })
      .catch(function() { setHistory([]); })
      .finally(function() { setHistoryLoading(false); });
  }, [activeTab]);

  // Build wa.me URL from phone number
  // Priority order: +XX → strip +  |  00XX → strip 00  |  10-digit → assume India (+91)  |  0X → assume India
  var buildWaUrl = function(phone, message) {
    var cleaned = (phone || '').replace(/[\s\-()+]/g, '');
    if (!cleaned) return null;
    if (cleaned.startsWith('00')) cleaned = cleaned.slice(2);       // 0044... → 44...
    else if (cleaned.length === 10) cleaned = '91' + cleaned;       // 10-digit local → India
    else if (cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1); // 0X... local → India
    if (!/^\d+$/.test(cleaned)) return null;                        // reject non-numeric
    var encoded = encodeURIComponent(message || '');
    return 'https://wa.me/' + cleaned + '?text=' + encoded;
  };

  // Strip HTML tags for WhatsApp plain text
  var stripHtml = function(html) {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
  };

  var whatsappUsers = recipients.filter(function(u) { return u.phone && u.phone.trim(); });
  var waMessage = subject ? subject + '\n\n' + stripHtml(bodyHtml) : stripHtml(bodyHtml);

  var roleColors = {
    admin: { bg: '#eaf4fb', color: '#2980b9' },
    healer: { bg: '#eafaf1', color: '#27ae60' },
    student: { bg: '#fef9e7', color: '#d4ac0d' },
    patient: { bg: '#f5eafb', color: '#8e44ad' },
    general: { bg: '#f0f0f0', color: '#666' }
  };

  var s = {
    layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
    main: { marginLeft: isMobile ? 0 : '260px', flex: 1, padding: isMobile ? '56px 16px 16px' : '32px' },
    header: { marginBottom: '28px' },
    title: { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
    sub: { fontSize: '13px', color: '#666', margin: 0 },
    card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '28px', marginBottom: '20px' },
    sectionTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', letterSpacing: '0.5px', marginBottom: '14px' },
    label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
    input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
    textarea: { width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', resize: 'vertical', minHeight: '200px', lineHeight: '1.7' },
    tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px', width: 'fit-content' },
    tab: { padding: '10px 28px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#666', cursor: 'pointer' },
    tabActive: { background: '#4a8f82', color: '#fff' },
    sendBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '13px 32px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
    waBtn: { background: '#25D366', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={s.main}>

        {/* Header */}
        <div style={s.header}>
          <h1 style={s.title}>Announcements</h1>
          <p style={s.sub}>Send mass communications to registered users</p>
        </div>

        {/* Tabs */}
        <div style={s.tabs}>
          <button style={activeTab === 'compose' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={function() { setActiveTab('compose'); }}>
            Compose & Send
          </button>
          <button style={activeTab === 'whatsapp' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={function() { setActiveTab('whatsapp'); }}>
            WhatsApp
          </button>
          <button style={activeTab === 'history' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={function() { setActiveTab('history'); }}>
            History
            {whatsappUsers.length > 0 && (
              <span style={{ marginLeft: '8px', background: '#25D366', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
                {whatsappUsers.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'compose' && (
          <div>
            {/* Step 1: Recipients */}
            <div style={s.card}>
              <div style={s.sectionTitle}>Step 1 — Select Recipients</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                {/* All toggle */}
                <button type="button" onClick={function() { toggleRole('all'); }}
                  style={{ padding: '8px 18px', border: '2px solid', borderRadius: '20px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px',
                    borderColor: selectedRoles.length === ALL_ROLES.length ? '#4a8f82' : '#e0e0e0',
                    background: selectedRoles.length === ALL_ROLES.length ? '#4a8f82' : '#fff',
                    color: selectedRoles.length === ALL_ROLES.length ? '#fff' : '#666' }}>
                  All Users
                </button>
                {ALL_ROLES.map(function(role) {
                  var active = selectedRoles.includes(role);
                  var c = roleColors[role];
                  return (
                    <button key={role} type="button" onClick={function() { toggleRole(role); }}
                      style={{ padding: '8px 18px', border: '2px solid', borderRadius: '20px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'capitalize', letterSpacing: '0.5px',
                        borderColor: active ? c.color : '#e0e0e0',
                        background: active ? c.bg : '#fff',
                        color: active ? c.color : '#999' }}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </button>
                  );
                })}
              </div>

              {/* Recipient count */}
              <div style={{ background: '#f9faf7', borderRadius: '6px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                {loadingRecipients ? (
                  <span style={{ fontSize: '13px', color: '#999' }}>Loading recipients...</span>
                ) : selectedRoles.length === 0 ? (
                  <span style={{ fontSize: '13px', color: '#999' }}>Select at least one role to see recipients</span>
                ) : (
                  <>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '24px', fontWeight: '700', color: '#4a8f82', lineHeight: 1 }}>{recipientCount}</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>recipients selected</div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                        {recipients.filter(function(u) { return u.email; }).length} with email ·{' '}
                        {recipients.filter(function(u) { return u.phone; }).length} with phone (WhatsApp)
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Step 2: Compose */}
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={s.sectionTitle}>Step 2 — Compose Message</div>
                <button type="button" onClick={function() { setPreviewMode(!previewMode); }}
                  style={{ background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '6px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', color: '#666' }}>
                  {previewMode ? 'Edit' : 'Preview Email'}
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Subject *</label>
                <input style={s.input} type="text"
                  placeholder={'e.g. Important Update from ' + (process.env.REACT_APP_CENTRE_NAME || 'our centre')}
                  value={subject} onChange={function(e) { setSubject(e.target.value); }} />
              </div>

              {previewMode ? (
                <div>
                  <label style={s.label}>Email Preview</label>
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ background: 'linear-gradient(135deg, #071e12, #0c2617)', padding: '20px 28px' }}>
                      <img src={process.env.REACT_APP_CENTRE_LOGO_URL} alt={process.env.REACT_APP_CENTRE_NAME} style={{ height: '36px' }} />
                    </div>
                    <div style={{ height: '3px', background: 'linear-gradient(to right, #00AEEF, #4a8f82, #8DC63F)' }}></div>
                    <div style={{ padding: '28px 32px', fontSize: '14px', color: '#333', lineHeight: '1.8' }}
                      dangerouslySetInnerHTML={{ __html: bodyHtml || '<span style="color:#999">No content yet...</span>' }} />
                    <div style={{ background: '#f9faf7', padding: '18px 28px', fontSize: '11px', color: '#999', textAlign: 'center', borderTop: '1px solid #e0e0e0' }}>
                      {process.env.REACT_APP_CENTRE_NAME} | {process.env.REACT_APP_CENTRE_ADDRESS}<br />
                      Phone: {process.env.REACT_APP_CENTRE_PHONE} | Email: {process.env.REACT_APP_CENTRE_EMAIL}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label style={s.label}>Email Body * (HTML supported)</label>
                  <textarea style={s.textarea}
                    placeholder={'Write your announcement here...\n\nYou can use basic HTML like <b>bold</b>, <br> for line breaks, <a href="...">links</a>, etc.'}
                    value={bodyHtml} onChange={function(e) { setBodyHtml(e.target.value); }} />
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>
                    Tip: Use &lt;b&gt;bold&lt;/b&gt;, &lt;br&gt; for new lines, &lt;a href="..."&gt;link text&lt;/a&gt; for hyperlinks. Your message will be wrapped in the PWC branded email template.
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Send */}
            <div style={s.card}>
              <div style={s.sectionTitle}>Step 3 — Send</div>
              {result ? (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ background: result.failed === 0 ? '#eafaf1' : '#fef9e7', border: '1px solid ' + (result.failed === 0 ? '#27ae60' : '#d4ac0d'), borderRadius: '6px', padding: '16px 20px' }}>
                    <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: result.failed === 0 ? '#1a5c38' : '#7D6608', marginBottom: '8px' }}>
                      {result.failed === 0 ? 'Announcement sent successfully!' : 'Sent with some failures'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#555', display: 'flex', gap: '24px' }}>
                      <span>Sent: <strong style={{ color: '#27ae60' }}>{result.sent}</strong></span>
                      {result.failed > 0 && <span>Failed: <strong style={{ color: '#e74c3c' }}>{result.failed}</strong></span>}
                      <span>Total: <strong>{result.total}</strong></span>
                    </div>
                  </div>
                  <button onClick={function() { setResult(null); setSubject(''); setBodyHtml(''); setSelectedRoles([]); }}
                    style={{ marginTop: '12px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: '#666' }}>
                    New Announcement
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button onClick={handleSend} disabled={sending || !selectedRoles.length || !subject.trim() || !bodyHtml.trim()}
                    style={{ ...s.sendBtn, opacity: (sending || !selectedRoles.length || !subject.trim() || !bodyHtml.trim()) ? 0.5 : 1, cursor: (sending || !selectedRoles.length || !subject.trim() || !bodyHtml.trim()) ? 'not-allowed' : 'pointer' }}>
                    {sending ? 'Sending...' : 'Send Announcement'}
                  </button>
                  {recipientCount > 0 && !sending && (
                    <span style={{ fontSize: '13px', color: '#999' }}>Will send to {recipientCount} recipient(s)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'whatsapp' && (
          <div>
            {selectedRoles.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '48px' }}>
                <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>No recipients selected</div>
                <div style={{ fontSize: '13px', color: '#999' }}>Go to Compose & Send tab and select roles first.</div>
              </div>
            ) : whatsappUsers.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '48px' }}>
                <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>No phone numbers found</div>
                <div style={{ fontSize: '13px', color: '#999' }}>None of the {recipientCount} selected users have a phone number on record.</div>
              </div>
            ) : (
              <div>
                {/* Message preview */}
                <div style={s.card}>
                  <div style={s.sectionTitle}>Message Preview (Plain Text for WhatsApp)</div>
                  <div style={{ background: '#f9faf7', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', fontSize: '13px', color: '#333', lineHeight: '1.8', whiteSpace: 'pre-wrap', maxHeight: '160px', overflowY: 'auto' }}>
                    {waMessage || <span style={{ color: '#999' }}>Enter subject and body in Compose tab to preview the WhatsApp message.</span>}
                  </div>
                  {waMessage && (
                    <button onClick={function() { navigator.clipboard.writeText(waMessage); alert('Message copied to clipboard!'); }}
                      style={{ marginTop: '10px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '7px 16px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', color: '#666' }}>
                      Copy Message Text
                    </button>
                  )}
                </div>

                {/* User list with wa.me links */}
                <div style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={s.sectionTitle}>{whatsappUsers.length} Users with Phone Numbers</div>
                    <span style={{ fontSize: '12px', color: '#999' }}>Click "Open WhatsApp" to message individually</span>
                  </div>
                  <div style={{ background: '#fef9e7', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#7D6608', lineHeight: '1.6' }}>
                    <strong>Phone number formats supported:</strong> numbers with country code prefix are handled automatically (e.g. <code>+91</code>, <code>+1</code>, <code>+44</code>, <code>0044</code>). 10-digit numbers without a prefix are assumed to be Indian (+91). For international users, store phone numbers with the <strong>+ country code</strong> (e.g. +1 555 123 4567).
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    {whatsappUsers.map(function(u, i) {
                      var waUrl = buildWaUrl(u.phone, waMessage);
                      var c = roleColors[u.role] || { bg: '#f0f0f0', color: '#666' };
                      return (
                        <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 14px', background: i % 2 === 0 ? '#fafafa' : '#fff', borderRadius: '4px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>
                            {u.firstName ? u.firstName.charAt(0) : '?'}{u.lastName ? u.lastName.charAt(0) : ''}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{u.firstName} {u.lastName}</div>
                            <div style={{ fontSize: '11px', color: '#999' }}>{u.phone}</div>
                          </div>
                          <span style={{ fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', textTransform: 'capitalize', background: c.bg, color: c.color }}>{u.role}</span>
                          {waUrl ? (
                            <a href={waUrl} target="_blank" rel="noopener noreferrer" style={s.waBtn}>
                              Open WhatsApp
                            </a>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#e74c3c' }}>Invalid number</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#999', fontSize: '14px' }}>Loading history...</div>
            ) : history.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '60px', color: '#bbb' }}>
                <div style={{ fontSize: '15px', marginBottom: '6px' }}>No announcements sent yet</div>
                <div style={{ fontSize: '13px' }}>Past announcements will appear here after you send them.</div>
              </div>
            ) : history.map(function(ann) {
              var isOpen = expandedHistory === ann._id;
              return (
                <div key={ann._id} style={{ ...s.card, padding: '0', overflow: 'hidden', marginBottom: '12px' }}>
                  <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', cursor: 'pointer' }}
                    onClick={function() { setExpandedHistory(isOpen ? null : ann._id); }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '14px', color: '#1a1a2e', marginBottom: '4px' }}>{ann.subject}</div>
                      <div style={{ fontSize: '12px', color: '#888', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <span>Sent by <strong>{ann.sentByName}</strong></span>
                        <span>{new Date(ann.sentAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        <span>To: {(ann.roles || []).join(', ')}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ background: '#eafaf1', color: '#27ae60', borderRadius: '10px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' }}>{ann.sent} sent</span>
                      {ann.failed > 0 && <span style={{ background: '#fdf0f0', color: '#e74c3c', borderRadius: '10px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' }}>{ann.failed} failed</span>}
                      <span style={{ color: '#bbb', fontSize: '14px' }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 20px', background: '#f9faf7' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '10px' }}>Email Body Preview</div>
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', fontSize: '13px', color: '#333', lineHeight: '1.7', maxHeight: '300px', overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: ann.bodyHtml }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};

export default Announcements;
